import React, { useState } from "react";
import { Check, Loader2, X, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import tokenManager from "../../utils/tokenManager";

// ─── Demo Login Modal ────────────────────────────────────────────────────────

const DemoModal = ({ onClose, onSuccess }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await apiService.demoLogin({ name, email, companyName });

      if (response.success) {
        tokenManager.setTokens(
          response.accessToken,
          response.refreshToken,
          response.csrfToken,
        );
        if (response.fingerprint) {
          tokenManager.setFingerprint(response.fingerprint);
        }
        if (response.user) {
          tokenManager.setUser(response.user);
        }
        onSuccess();
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to start demo. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-md rounded-2xl border border-[#22262F] p-8"
        style={{ background: "#13161B" }}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div
              className="flex items-center justify-center w-8 h-8 rounded-lg"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
              }}
            >
              <Sparkles size={14} className="text-white" />
            </div>
            <span className="text-xs font-medium text-purple-400 uppercase tracking-wider">
              Free Preview
            </span>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-1">
            Try the demo
          </h2>
          <p className="text-sm text-gray-400">
            Explore the full CRM with pre-loaded data. No credit card needed.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Your name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm"
              placeholder="e.g. Sarah Johnson"
              required
              disabled={loading}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Work email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm"
              placeholder="you@company.com"
              required
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Company name
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full px-3 py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm"
              placeholder="e.g. Acme Corp"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 text-white rounded-lg font-medium transition-all duration-300 mt-2 relative hover:scale-[1.02] text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
              border: "1px solid",
              borderImageSource:
                "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
              boxShadow: `
                0px 36px 32px -16px #427BCA36,
                0px 8px 16px 0px #2F578F1F,
                0px 2px 4px -8px #427BCA14,
                0px 8px 24px -16px #FFFFFF3D inset,
                0px -24px 32px 0px #FFFFFF38 inset,
                0px 0px 12px 0px #FFFFFF inset
              `,
              borderImageSlice: "1",
            }}
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4" />
                  Setting up your demo...
                </>
              ) : (
                "Start free demo"
              )}
            </span>
          </button>
        </form>

        {/* Footer note */}
        <p className="text-xs text-gray-600 text-center mt-4">
          Demo data is reset nightly. Your details won't be shared.
        </p>
      </div>
    </div>
  );
};

// ─── Main Login Component ────────────────────────────────────────────────────

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const navigate = useNavigate();

  const handleForgot = () => {
    navigate("/forgot-password");
  };

  const handleGoogleLogin = () => {
    window.location.href = `${
      process.env.REACT_APP_API_URL || "http://localhost:5000"
    }/api/auth/google`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.endsWith("@fibrecrm.com")) {
      setError("Only @fibrecrm.com email addresses are allowed.");
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.login(
        email,
        password,
        remember,
        requires2FA ? twoFactorCode : null,
      );

      if (response.requiresTwoFactor) {
        setRequires2FA(true);
        setError("");
        setLoading(false);
        return;
      }

      if (response.success) {
        tokenManager.setUser(response.user);

        if (!response.user.company?.workspaceUrl) {
          navigate("/onboarding");
        } else {
          navigate("/dashboard");
        }
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(
        err.response?.data?.message || "Failed to login. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDemoSuccess = () => {
    navigate("/dashboard");
  };

  return (
    <>
      {showDemoModal && (
        <DemoModal
          onClose={() => setShowDemoModal(false)}
          onSuccess={handleDemoSuccess}
        />
      )}

      <div className="max-h-screen flex flex-col lg:flex-row">
        {/* Left side - Login Form */}
        <div className="w-full lg:w-2/5 flex items-center justify-center px-6 sm:px-8 md:px-12 py-8 lg:py-0">
          <div className="w-full max-w-md">
            {/* Logo */}
            <div className="mb-8 sm:mb-12 lg:mb-16">
              <div className="text-lg sm:text-xl w-20">
                <img
                  src="/assets/logo.png"
                  alt="Dashboard Preview"
                  className="w-auto h-auto"
                />
              </div>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit}>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white mb-2 sm:mb-3">
                Log in
              </h1>
              <p className="text-gray-400 mb-6 sm:mb-8 text-sm sm:text-base">
                Welcome back! Please enter your details.
              </p>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-400">{error}</p>
                </div>
              )}

              <div className="space-y-4 sm:space-y-5">
                {/* Email Field */}
                {/* <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm sm:text-base"
                    placeholder="Enter your email"
                    required
                    disabled={loading}
                  />
                </div> */}

                {/* Password Field */}
                {/* <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm sm:text-base"
                    placeholder="••••••••"
                    required
                    disabled={loading}
                  />
                </div> */}

                {/* {requires2FA && (
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      {useBackupCode ? "Backup Code" : "Authenticator Code"}
                    </label>
                    <input
                      type="text"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      className="w-full px-3 py-2.5 sm:py-3 bg-[#0C0E12] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 text-sm sm:text-base"
                      placeholder={
                        useBackupCode
                          ? "Enter 8-character backup code"
                          : "Enter 6-digit code"
                      }
                      maxLength={useBackupCode ? 8 : 6}
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setUseBackupCode(!useBackupCode)}
                      className="text-xs text-purple-400 hover:text-purple-300 mt-2"
                    >
                      {useBackupCode
                        ? "Use authenticator code"
                        : "Use backup code"}
                    </button>
                  </div>
                )} */}

                {/* Remember me and Forgot password */}
                {/* <div className="flex items-center justify-between py-2 flex-wrap gap-2">
                  <label className="flex items-center">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="appearance-none w-4 h-4 rounded border border-gray-600 bg-[#0C0E12] checked:bg-purple-500 checked:border-purple-500 focus:ring-purple-500 focus:ring-2"
                        disabled={loading}
                      />
                      {remember && (
                        <Check
                          size={11}
                          className="absolute top-1 left-0.5 text-white pointer-events-none"
                        />
                      )}
                    </div>
                    <span className="ml-2 text-xs sm:text-sm text-white">
                      Remember for 30 days
                    </span>
                  </label>
                  <button
                    type="button"
                    className="text-xs sm:text-sm text-white hover:text-purple-300 bg-transparent border-none cursor-pointer"
                    onClick={handleForgot}
                    disabled={loading}
                  >
                    Forgot password
                  </button>
                </div> */}

                {/* Sign in Button */}
                {/* <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 sm:py-3 text-white rounded-lg font-medium transition-all duration-300 mt-4 sm:mt-6 relative 
                    hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background:
                      "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                    border: "1px solid",
                    borderImageSource:
                      "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                    boxShadow: `
                    0px 36px 32px -16px #427BCA36,
                    0px 8px 16px 0px #2F578F1F,
                    0px 2px 4px -8px #427BCA14,
                    0px 8px 24px -16px #FFFFFF3D inset,
                    0px -24px 32px 0px #FFFFFF38 inset,
                    0px 0px 12px 0px #FFFFFF inset
                  `,
                    borderImageSlice: "1",
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.background =
                        "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)";
                  }}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4" />
                        Signing in...
                      </>
                    ) : (
                      "Sign in"
                    )}
                  </span>
                </button> */}

                {/* Demo CTA */}
                <div className="relative flex items-center gap-3 pt-1">
                  <div className="flex-1 h-px bg-gray-800" />
                  <span className="text-xs text-gray-600 whitespace-nowrap">
                    or
                  </span>
                  <div className="flex-1 h-px bg-gray-800" />
                </div>

                <button
                  type="button"
                  onClick={() => setShowDemoModal(true)}
                  disabled={loading}
                  className="w-full py-2.5 sm:py-3 bg-transparent border border-gray-700 hover:border-gray-500 rounded-lg font-medium text-gray-300 hover:text-white flex items-center justify-center gap-2 transition-all duration-200 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sparkles size={15} className="text-purple-400" />
                  Try a free demo
                </button>
              </div>
            </form>

            {/* Footer */}
            <div className="mt-12 sm:mt-16 lg:mt-20">
              <p className="text-xs sm:text-sm text-gray-500">
                © OctopushX Ltd 2026
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Dashboard Image */}
        <div className="hidden lg:flex w-3/5 bg-[#13161B] items-center justify-center pt-[100px] pb-[100px] pl-[100px] pr-0 rounded-l-[42px] mt-5 mb-5">
          <div className="w-full h-full rounded-l-[10px] overflow-hidden border-l-[4px] border-t-[4px] border-b-[4px] border-[#22262F]">
            <img
              src="/assets/banner2.png"
              alt="Dashboard Preview"
              className="w-auto h-auto"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
